import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, inject, signal } from '@angular/core';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { FormBuilder, FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';

import {select, Store} from '@ngrx/store';
import {getAuthFlow, getError, getLoggedInUser, IAuthenticationState, LoginUser} from '@app/stores';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {UserAbilityService} from '@app/services/user-ability.service';
import {mergeAuthTranslations} from '../auth-i18n';
import { AuthMarketingPanelComponent } from '../auth-marketing-panel/auth-marketing-panel.component';

@Component({
  selector: 'app-temporary-login',
  templateUrl: './temporary-login.component.html',
  styleUrls: ['./temporary-login.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, RouterLink, AuthMarketingPanelComponent, TranslateModule]
})
export class TemporaryLoginComponent implements OnInit, OnDestroy {
  private formBuilder = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private authenticationStore = inject<Store<IAuthenticationState>>(Store);
  private userAbilityService = inject(UserAbilityService);
  private translate = inject(TranslateService);

  loginForm: FormGroup<{email: FormControl<string | null>; password: FormControl<string | null>}>;
  loading = false;
  submitted = false;
  returnUrl: string;
  authMessage: object;
  // True from the moment the credentials are sent until the store answers - with
  // a user, a login flow or an error. The button is disabled meanwhile, so one
  // click is one request.
  readonly loginPending = signal(false);
  unsubscriber = new Subject();

  constructor() {
    // redirect to home if already logged in
    this.authenticationStore.pipe(select(getLoggedInUser))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(user => {
        if (user) {
          this.loginPending.set(false);
          if (user.password_status === '1') {
            this.router.navigate(['/resetpassword']);
          } else {
            this.router.navigate([
              this.userAbilityService.resolvePostLoginUrl(user, this.returnUrl)
            ]);
          }
        }
      });

    // A refused attempt, or one the server answers with a challenge of its own
    // (MFA, a session conflict) rather than a user, still ends the attempt. Free
    // the button on either, or a single refusal would disable the form for good.
    this.authenticationStore.pipe(select(getError))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(error => {
        if (error) {
          this.loginPending.set(false);
        }
      });

    this.authenticationStore.pipe(select(getAuthFlow))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(flow => {
        if (flow) {
          this.loginPending.set(false);
        }
      });
  }

  ngOnInit() {
    mergeAuthTranslations(this.translate, this.unsubscriber);

    this.loginForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      // No length rule here: this form must accept whatever a legacy account still has,
      // or the people the server wants to send to the forced-reset screen never reach it.
      password: ['', [Validators.required]]
    });

    // Empty/`/` resolve to role default home after login.
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '';
    this.authMessage = {
      Success: false,
      msg: ''
    };
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.loginForm.controls;
  }

  /**
   * Returns Auth token object that will use for next navigations.
   *
   * @param  url  an absolute URL giving the reset Temporary login page of the Application.
   * @param  name the location of the request, relative to the url.
   * @return      success object with user data.
   */

  onSubmit() {
    this.submitted = true;

    // stop here if form is invalid, or the previous attempt has not answered yet
    if (this.loginForm.invalid || this.loginPending()) {
      return;
    }

    this.loginPending.set(true);
    this.authenticationStore.dispatch(LoginUser({
      credentials: {
        email: this.f.email.value,
        password: this.f.password.value
      }
    }));
  }

  ngOnDestroy() {
    this.unsubscriber.next(undefined);
    this.unsubscriber.complete();
  }
}
